class Tabs {
  tablist: HTMLElement;
  subTablists: HTMLElement[];
  tabs: HTMLElement[];
  panels: HTMLElement[];
  focusTimeout: ReturnType<typeof setTimeout>;
  activationDelay: number = 300;
  tiers: HTMLElement[];
  tabsContainer: HTMLElement;
  enlargeToggle: HTMLElement;

  keys = {
    down: 40,
    end: 35,
    home: 36,
    left: 37,
    right: 39,
    up: 38,
  };

  direction = {
    37: -1,
    38: -1,
    39: 1,
    40: 1,
  };

  constructor(element: HTMLElement) {
    this.tablist = element.querySelector('[role="tablist"]');
    this.subTablists = Array.from(element.querySelectorAll(".js-sub-tablist"));
    this.tabs = Array.from(element.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(element.querySelectorAll('[role="tabpanel"]'));
    this.tiers = Array.from(element.querySelectorAll(".js-tabs-tier"));
    this.tabsContainer = element.querySelector(".js-tabs-container");
    this.enlargeToggle = element.querySelector(".js-tab-enlarge-toggle");

    this.init();
  }

  init() {
    const activeTab: HTMLElement = this.tablist.querySelector(
      '[aria-selected="true"]',
    );

    // Scroll to active tabs if not first
    if (this.tabs.indexOf(activeTab) !== 0) {
      const tabOffset = activeTab.getBoundingClientRect().left;
      const tablistOffset = activeTab.offsetParent.getBoundingClientRect().left;
      this.tablist.scrollTo(tabOffset - tablistOffset, 0);
    }

    this.bindHanders();

    this.setMaxWidth();
  }

  setMaxWidth() {
    // Set max width of tiers, used to animate when panel is maximised
    let defaultMaxWidthOfTier;
    for (const tier of this.tiers) {
      if (!tier.hasAttribute("hidden")) {
        defaultMaxWidthOfTier = tier.offsetWidth;
      }

      tier.style.maxWidth = defaultMaxWidthOfTier + "px";
    }
  }

  bindHanders() {
    for (const tab of this.tabs) {
      tab.addEventListener("click", this.handleTabClick);
      tab.addEventListener("keydown", this.handleTabKeydown);
      tab.addEventListener("keyup", this.handleTabKeyup);
      tab.addEventListener("focus", this.handleTabFocus);
    }
    addEventListener("resize", this.handleResize);
    this.enlargeToggle.addEventListener("click", this.handleEnlargeToggleClick);
  }

  handleResize = (e) => {
    this.setMaxWidth();
  };

  handleTabClick = (e) => {
    const tab = e.target;
    this.activateTab(tab, false);
  };

  handleTabKeydown = (e) => {
    const key = e.keyCode;
    const keys = this.keys;

    switch (key) {
      case keys.end:
        e.preventDefault();
        // Activate last tab
        this.activateTab(this.tabs[this.tabs.length - 1]);
        break;
      case keys.home:
        e.preventDefault();
        // Activate first tab
        this.activateTab(this.tabs[0]);
        break;

      // Up and down are in keydown because we need to prevent page scroll
      case keys.up:
      case keys.down:
        e.preventDefault();
        this.determineOrientation(e);
        break;
    }
  };

  handleTabKeyup = (e) => {
    const key = e.keyCode;
    const keys = this.keys;

    switch (key) {
      case keys.left:
      case keys.right:
        this.determineOrientation(e);
        break;
    }
  };

  handleTabFocus = (e) => {
    const target = e.target;

    clearTimeout(this.focusTimeout);

    this.focusTimeout = setTimeout(() => {
      const focused = document.activeElement;

      if (target === focused) {
        this.activateTab(target, false);
      }
    }, this.activationDelay);
  };

  activateTab(tab: HTMLElement, setFocus: boolean = true) {
    this.deactivateTabs();
    tab.setAttribute("aria-selected", "true");

    const controls = tab.getAttribute("aria-controls");

    document.getElementById(controls).removeAttribute("hidden");

    if (tab.hasAttribute("aria-owns")) {
      const owns = tab.getAttribute("aria-owns");

      for (const subTablist of this.subTablists) {
        subTablist.setAttribute("hidden", "hidden");
      }

      if (
        document.getElementById(owns) &&
        document.getElementById(owns).hasAttribute("hidden")
      ) {
        document.getElementById(owns).removeAttribute("hidden");
      }
    }
    if (tab.hasAttribute("data-owner")) {
      const owner = tab.getAttribute("data-owner");

      document.getElementById(owner).setAttribute("aria-selected", "true");
    }

    if (setFocus) {
      tab.focus();
    }
  }

  deactivateTabs() {
    for (const tab of this.tabs) {
      tab.setAttribute("aria-selected", "false");
    }

    for (const panel of this.panels) {
      panel.setAttribute("hidden", "hidden");
    }
  }

  handleEnlargeToggleClick = (e) => {
    e.stopPropagation();
    e.target.classList.toggle("active");
    this.tabsContainer.classList.toggle(
      "tabbed-content-panel__container--enlarged",
    );
  };

  switchTabOnArrowPress = (e) => {
    const pressed = e.keyCode;
    const keys = this.keys;
    const direction = this.direction[pressed];
    const tabs = this.tabs;

    if (direction) {
      const target = e.target;
      const index = this.tabs.indexOf(target);

      if (index !== undefined) {
        if (tabs[index + direction]) {
          tabs[index + direction].focus();
        } else if (pressed === keys.left || pressed === keys.up) {
          tabs[tabs.length - 1].focus();
        } else if (pressed === keys.right || pressed === keys.down) {
          tabs[0].focus();
        }
      }
    }
  };

  determineOrientation = (e) => {
    const key = e.keyCode;
    const keys = this.keys;
    const vertical =
      this.tablist.getAttribute("aria-orientation") === "vertical";
    let proceed = false;

    if (vertical) {
      if (key === keys.up || key === keys.down) {
        proceed = true;
      }
    } else {
      if (key === keys.left || key === keys.right) {
        proceed = true;
      }
    }

    if (proceed) {
      this.switchTabOnArrowPress(e);
    }
  };
}

export default Tabs;
